'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { type Howl, Howler } from 'howler'
import {
  useGetMasterVolume,
  useGetMusicVolume,
  useGetIsMusicMuted,
  useGetIsMasterMuted,
  useGetCurrentSongIndex,
  useSoundActions,
  useGetIsPlayingMusic,
  useSoundStore
} from 'src/store/sound'
import { useInitialUserInteraction } from 'src/hooks/useInitialUserInteraction'
import { useGameMusic } from 'src/hooks/useGameMusic'
import { useGetCurrentGame } from 'src/store/game'
import { isMarbleGame } from 'src/types/domain/Game'
import { isCCTVGameType } from 'src/utils/gameTypeUtils'
import { useClearMediaSession } from 'src/hooks/useClearMediaSession'
import { clearMediaSession } from 'src/utils/clearMediaSession'
import { useDeviceInfo } from 'src/hooks/useDeviceInfo'
import { toSafeVolume } from 'src/utils/volumeUtils'

// The page at /world has its own music and effects, so the widget's music is silent
// on its route. An exact match on purpose: a game id that merely starts with
// "world" is a game like any other, and `isWorldRoute` below is false on
// every route that existed before the world did.
const WORLD_ROUTE = '/world'

export function Music() {
  const soundStoreActions = useSoundActions()
  const isWorldRoute = usePathname() === WORLD_ROUTE
  const isPlayingMusic = useGetIsPlayingMusic()
  const currentGame = useGetCurrentGame()
  const currentSongIndex = useGetCurrentSongIndex()
  const { isMobile, isTablet } = useDeviceInfo()

  const masterVolume = useGetMasterVolume()
  const musicVolume = useGetMusicVolume()
  // Clamped: this value is assigned to HTMLMediaElement.volume (the HTML5
  // fallback below), which throws on non-finite or out-of-range input.
  const volume = toSafeVolume(musicVolume * masterVolume, 0)

  const isMasterMuted = useGetIsMasterMuted()
  const isMusicMuted = useGetIsMusicMuted()
  const muted = isMasterMuted || isMusicMuted

  const gameType = isMarbleGame(currentGame) ? currentGame.track.gameType : 'marbles'
  const songs = useGameMusic(gameType)
  const currentSong = songs[currentSongIndex]

  const activeHowlRef = useRef<Howl | null>(null)
  const activeSoundIdRef = useRef<number | null>(null)
  const currentPlaylistRef = useRef<string | null>(null)
  const prevGameTypeRef = useRef<string>(gameType)

  // CCTV games (irl, irl_alt, duck_river, snow_run, social_betting) and
  // crash stream their music inside the clip/livestream audio and have no
  // separate music track, so the Music channel is force-muted there (the
  // dialog also hides its control). When the player moves to a classic game
  // that DOES have music, undo that auto-mute — but only on the
  // transition, so they can still mute music themselves on a classic
  // game. Lives here (always mounted) rather than the audio dialog,
  // which only mounts while open.
  const prevHasMusicRef = useRef<boolean | null>(null)
  useEffect(() => {
    const hasMusic = !isCCTVGameType(gameType)
    const prev = prevHasMusicRef.current
    prevHasMusicRef.current = hasMusic
    const musicMuted = useSoundStore.getState().muted.music
    if (!hasMusic) {
      if (!musicMuted) soundStoreActions.muteMusic()
    } else if (prev === false && musicMuted) {
      soundStoreActions.unmuteMusic()
    }
  }, [gameType, soundStoreActions])

  useInitialUserInteraction(() => {
    // irl_alt (Rush Hour) / duck_river / snow_run / social_betting / crash
    // have their own stream audio — never auto-play widget music
    if (
      gameType === 'irl_alt' ||
      gameType === 'duck_river' ||
      gameType === 'snow_run' ||
      gameType === 'social_betting' ||
      gameType === 'crash'
    )
      return

    const isRuntimePlaying = (Howler as any)._howls?.some((howl: any) => howl.playing?.()) ?? false

    if (!isRuntimePlaying && !isPlayingMusic && songs.length > 0) {
      soundStoreActions.playMusic()
      soundStoreActions.unmuteMaster()
    }
  })

  useClearMediaSession()

  // Handle game type transitions for irl_alt (Rush Hour)
  useEffect(() => {
    const prev = prevGameTypeRef.current
    prevGameTypeRef.current = gameType

    if (prev === gameType) return

    // Switching TO irl_alt/duck_river/snow_run/social_betting/crash — kill
    // widget music, stream has its own audio
    if (
      gameType === 'irl_alt' ||
      gameType === 'duck_river' ||
      gameType === 'snow_run' ||
      gameType === 'social_betting' ||
      gameType === 'crash'
    ) {
      const howl = activeHowlRef.current
      if (howl) {
        try {
          // Mute the HTML5 node directly first to prevent any audio leak
          const node = (howl as any)?._sounds?.[0]?._node as HTMLAudioElement | undefined
          if (node) {
            node.muted = true
            node.pause()
          }
          howl.stop()
          howl.unload()
        } catch (e) {
          console.warn('Error stopping music for irl_alt:', e)
        }
        activeHowlRef.current = null
        activeSoundIdRef.current = null
      }
      soundStoreActions.stopMusic()
    }

    // Switching FROM irl_alt — resume widget music for the new game type
    // Don't gate on songs.length — useGameMusic loads async, songs may still
    // be empty here. The playback effect will pick it up once songs arrive.
    if (
      (prev === 'irl_alt' ||
        prev === 'duck_river' ||
        prev === 'snow_run' ||
        prev === 'social_betting' ||
        prev === 'crash') &&
      gameType !== 'irl_alt' &&
      gameType !== 'duck_river' &&
      gameType !== 'snow_run' &&
      gameType !== 'social_betting' &&
      gameType !== 'crash'
    ) {
      soundStoreActions.playMusic()
    }
  }, [gameType, soundStoreActions])

  useEffect(() => {
    if (currentPlaylistRef.current !== gameType) {
      const prevHowl = activeHowlRef.current
      if (prevHowl) {
        try {
          prevHowl.stop()
        } catch (e) {
          console.warn('Error stopping previous playlist song:', e)
        }

        activeHowlRef.current = null
        activeSoundIdRef.current = null
      }

      currentPlaylistRef.current = gameType
      soundStoreActions.setPlaylistKey(gameType)
    }
  }, [gameType, songs, soundStoreActions])

  // The random starting track is chosen when a playlist ARRIVES, not when the game changes.
  // The playlist is fetched, so at the moment the game changes `songs` is still the previous
  // game's array; picking from its length left `songs[index]` undefined whenever the new
  // playlist was shorter (Birdie has three tracks, marbles five) — the play effect below
  // bailed on `!currentSong` and the game had no music, or kept whatever was already
  // playing. Keyed on the array's identity: one pick per loaded playlist, none for the
  // empty array while a fetch is in flight.
  const startedForRef = useRef<Howl[] | null>(null)
  useEffect(() => {
    if (songs.length === 0 || startedForRef.current === songs) return
    startedForRef.current = songs
    soundStoreActions.musicSongChanged(Math.floor(Math.random() * songs.length))
  }, [songs, soundStoreActions])

  // Entering /world: stop what is playing. Only the SOUND is gated by the
  // route (here, in the playback effect below and in the mobile resume).
  // The first-interaction callback above is deliberately left alone, and
  // `isPlayingMusic` is not touched: autoplay is armed ONCE per page life and
  // this component never remounts on an in-app navigation, so a player whose
  // first tap was in the world must still get music when a portal takes them
  // into a classic game. With the store left as it is, the playback effect
  // starts the song the moment the route is a game's again, exactly as on a
  // game opened directly.
  useEffect(() => {
    if (!isWorldRoute) return

    const howl = activeHowlRef.current
    if (!howl) return

    try {
      // Mute the HTML5 node directly first to prevent any audio leak, as the
      // switch to a game with stream audio does above
      const node = (howl as unknown as { _sounds?: { _node?: HTMLAudioElement }[] })._sounds?.[0]
        ?._node
      if (node) {
        node.muted = true
        node.pause()
      }
      howl.stop()
      howl.unload()
    } catch (e) {
      console.warn('Error stopping music for the world:', e)
    }
    activeHowlRef.current = null
    activeSoundIdRef.current = null
  }, [isWorldRoute])

  useEffect(() => {
    if (
      isWorldRoute ||
      gameType === 'irl_alt' ||
      gameType === 'duck_river' ||
      gameType === 'snow_run' ||
      gameType === 'social_betting' ||
      gameType === 'crash' ||
      !isPlayingMusic ||
      !currentSong ||
      muted
    )
      return

    const currentHowl = activeHowlRef.current
    const currentSoundId = activeSoundIdRef.current

    if (currentHowl && currentSoundId !== null) {
      try {
        currentHowl.stop(currentSoundId)
      } catch (e) {
        console.warn('Error stopping previous song:', e)
      }
    }

    try {
      const newSoundId = currentSong.play()
      currentSong.volume(volume, newSoundId)

      activeHowlRef.current = currentSong
      activeSoundIdRef.current = newSoundId

      // HTML5 fallback
      setTimeout(() => {
        const node = (currentSong as any)?._sounds?.[0]?._node as HTMLAudioElement | undefined
        if (node) {
          node.muted = false
          node.volume = volume
        }
      }, 0)

      const handleSongEnd = () => {
        const nextIndex = (currentSongIndex + 1) % songs.length
        soundStoreActions.musicSongChanged(nextIndex)
      }

      currentSong.on('end', handleSongEnd)
      return () => {
        currentSong.off('end', handleSongEnd)
      }
    } catch (e) {
      console.warn('Error playing new song:', e)
    }
  }, [
    currentSong,
    currentSongIndex,
    gameType,
    isPlayingMusic,
    isWorldRoute,
    muted,
    soundStoreActions,
    songs.length
  ])

  useEffect(() => {
    const howl = activeHowlRef.current
    const soundId = activeSoundIdRef.current

    if (!howl || soundId === null) return

    try {
      howl.volume(volume, soundId)
      howl.mute(muted, soundId)

      const node = (howl as any)?._sounds?.[0]?._node as HTMLAudioElement | undefined
      if (node) {
        node.muted = muted
        node.volume = muted ? 0 : volume
      }
    } catch (e) {
      console.warn('[Music] Failed to sync volume/mute:', e)
    }
  }, [muted, volume])

  useEffect(() => {
    if (!isMobile && !isTablet) return
    // On /world there is no widget music to stop on hide (it was stopped
    // on the way in) and none to resume on show. Not listening at all there
    // also keeps `isPlayingMusic` as it was, so a phone that left the world
    // for another app and came back still has music in the next classic game.
    if (isWorldRoute) return

    const stopAndUnload = () => {
      const currentHowl = activeHowlRef.current
      const currentSoundId = activeSoundIdRef.current

      if (currentHowl && currentSoundId !== null) {
        try {
          currentHowl.stop(currentSoundId)
          currentHowl.unload()
        } catch (e) {
          console.warn('[Music][Mobile] Failed to stop music on hide:', e)
        }

        activeHowlRef.current = null
        activeSoundIdRef.current = null
      }

      clearMediaSession()

      soundStoreActions.stopMusic()
    }

    const resumeMusic = () => {
      if (
        gameType === 'irl_alt' ||
        gameType === 'duck_river' ||
        gameType === 'snow_run' ||
        gameType === 'social_betting' ||
        gameType === 'crash'
      )
        return
      const isResumed = (Howler as any)._howls?.some((howl: any) => howl.playing?.()) ?? false
      if (!muted && songs.length > 0 && !isResumed) {
        soundStoreActions.playMusic()
      }
    }

    const handleVisibility = () => {
      const state = document.visibilityState
      if (state === 'hidden') {
        stopAndUnload()
      }

      if (state === 'visible') {
        resumeMusic()
      }
    }

    const handlePageShow = () => {
      resumeMusic()
    }

    const handlePageHide = () => {
      stopAndUnload()
    }

    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('pageshow', handlePageShow)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('pagehide', handlePageHide)
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [gameType, isMobile, isTablet, isWorldRoute, muted, songs, soundStoreActions])

  return null
}
